import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { LogCategory, LogType, LogWidget, LoggerService } from 'src/app/core/services/logger.service';
import { TransferCreditsService } from 'src/app/services/transfer-credits.service';
import { UtilityService } from '../../../core/services/utility.service';

@Component({
    selector: 'app-approval-edit-request',
    templateUrl: './approval-edit-request.component.html',
    styleUrls: ['./approval-edit-request.component.scss'],
    standalone: false
})
export class ApprovalEditRequestComponent implements OnInit, OnDestroy {
    @Input() courseId: any;
    @Input() request: any;

    public acceptedFileTypes: string[] = [".pdf", ".doc", ".docx", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/pdf"]
    public course$: Observable<any>;
    public tcSubmit$: Observable<any>;
    public showAoE: boolean = false;
    public uncCourses: any[] = [];

    @Output() close = new EventEmitter<void>();

    @ViewChild('syllabusInput') syllabusInput: ElementRef;

    constructor(
        private logger: LoggerService,
        public transferCredits: TransferCreditsService,
        public activeModal: NgbActiveModal, //  used for editing an existing request
        private toastr: ToastrService,
    ) {}

    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.Widget, LogWidget.TransferCredits, category);
    }
       
    public ngOnDestroy(): void {          
        this.log("ngOnDestroy", "", LogCategory.Destroy);
    }

    public ngOnInit(): void {
        this.log("ngOnInit", null, LogCategory.Init);

        let courseId = !!this.request ? this.request.ExchangeCourseId : this.courseId;
        this.transferCredits.courseId$.next(courseId);

        this.course$ = combineLatest([this.transferCredits.course$, this.transferCredits.lookups$]).pipe(
            map(([course, lookups]) => {
                if (!!this.request) {
                    course.RequestId = this.request.Id;
                    course.HostCourseNumber = this.request.HostCourseNumber;
                    course.AreaOfEmphasis = this.request.AreaOfEmphasis;
                    course.hasAoE = !!this.request.AreaOfEmphasis;
                    course.AoeJustification = this.request.AoeJustification;
                    course.SyllabusFilename = this.request.SyllabusFilename;
                    course.SyllabusFileType = this.request.SyllabusFileType;
                    course.Term = this.request.Term;
                    course.GeRequirement = this.request.GeRequirement;

                    if (!!this.request.HostCourseNumber?.trim()
                        && (!course.HostCourseNumbers.includes(this.request.HostCourseNumber) || (course?.ApprovalStatus != 'Approved'))) {
                        course.otherHostCourseNumber = this.request.HostCourseNumber;
                        course.HostCourseNumber = 'Other';
                    }
                }
                else
                    course.hasAoE = !!course.AreasOfEmphasis?.length;

                lookups.discontinuedAreas = lookups.emphasisOptionsList
                    .filter(e => e.Key.includes('(discontinued)'))
                    .map(e => e.Value);

                //  If we need to apply grouping to distinguish between approved, active, and discontinued areas
                //  then add activeAreas to the lookups.
                if (course.AreaOfEmphasis?.length || lookups.discontinuedAreas ?.length) {
                    lookups.activeAreas = lookups.emphasisOptionsList
                        .filter(e => !e.Key.includes('(discontinued)'))
                        .map(e => e.Value);
                }

                //  Construct the dropdown for the UNC Course Name list
                this.uncCourses = lookups.ubpCoreList.map(c => ({ name: c.Key, number: c.Value, display: `${c.Value} - ${c.Key}` }))
                    .sort((a, b) => a.display.localeCompare(b.display));

                this.uncCourses = [...this.uncCourses,
                    { name: 'General Business Elective Credit', number: 'BUSI ----', display: 'General Business Elective Credit' },
                    { name: 'UNC Equivalent Business Elective Credit', number: '', display: 'UNC Equivalent Business Elective Credit' }, //  custom course number
                    { name: 'General Education Requirement', number: '', display: 'General Education Requirement' }, // gen ed credit determines course number
                    { name: 'General, Non-Business Elective Credit', number: 'GENR ----', display: 'General, Non-Business Elective Credit' }, 
                    { name: 'Other', number: '', display: 'Other' },//  custom course number
                ]

                let selected = null;
                course.otherPrimaryCourseName = "";

                if (!!course.GeRequirement) {
                    selected = lookups.ubpGenEdList.find(c => c.Value == course.UncPrimaryCourseNumber);

                    if (!!selected) {
                        course.UncPrimaryCourseName = this.uncCourses.find(c => c.name == 'General Education Requirement');
                        course.GeRequirement = selected;
                    }
                }

                //  Populate the dropdowns accordingly based on the course that's passed in
                if (!!course.UncPrimaryCourseName && !course.UncPrimaryCourseName?.display) {
                    selected = this.uncCourses.find(c => (c.name == course.UncPrimaryCourseName));

                    if (!!selected)
                        course.UncPrimaryCourseName = selected;
                }

                if (!selected && !!course.UncPrimaryCourseName) {
                    course.otherPrimaryCourseName = course.UncPrimaryCourseName;

                    if (course.UncPrimaryCourseNumber?.toLowerCase().startsWith('busi'))
                        course.UncPrimaryCourseName = this.uncCourses.find(c => (c.name == 'UNC Equivalent Business Elective Credit'));
                    else
                        course.UncPrimaryCourseName = this.uncCourses.find(c => (c.name == 'Other'));
                }

                let requestedCourse = Object.assign({}, course);
                requestedCourse.isDirty = false;
                requestedCourse.validate = () => this.validate(requestedCourse, course, lookups);
                requestedCourse.onChange = () => this.onChange(requestedCourse);

                this.checkAoE(requestedCourse);
                requestedCourse.validate();

                return requestedCourse;
            })
        )
    }

    public onUncCourseSelect(obj: any, course: any) {
        if (!!obj) {
            course.UncPrimaryCourseNumber = obj.number;

            if (obj.display != 'General Education Requirement')
                course.GeRequirement = '';
        }
        else
            course.UncPrimaryCourseNumber = '';

        this.checkDepartment(course);
        course.onChange();
    }

    public onGenEdSelect(obj: any, course: any) {
        if (!!obj)
            course.UncPrimaryCourseNumber = obj.Value;

        course.onChange();
    }

    // This keeps the original order when using the keyvalue pipe
    public preserveOrder = () => 0;

    public onChange(obj: any) {
        obj.isDirty = true;
        this.checkAoE(obj);
        obj.validate();
    }

    private checkAoE(obj: any) {
        this.showAoE = ((obj.UncPrimaryCourseName?.display == 'General Business Elective Credit') || (obj.UncPrimaryCourseName == 'General Business Elective Credit'))
            || (obj.isNew && ((obj.UncPrimaryCourseName?.display == 'UNC Equivalent Business Elective Credit') || (obj.UncPrimaryCourseName == 'UNC Equivalent Business Elective Credit')))
    }

    private checkDepartment(obj: any) {
        const courseName = obj.UncPrimaryCourseName?.display ?? obj.UncPrimaryCourseName;

        if (courseName == 'General Education Requirement')
            obj.UncDepartment = 'General Education Requirement';

        else if (courseName == 'General, Non-Business Elective Credit')
            obj.UncDepartment = 'General Elective Credit';

        else if ((courseName == 'General Business Elective Credit') || (courseName == 'UNC Equivalent Business Elective Credit')
            || (courseName?.startsWith('BUSI '))){
            obj.UncDepartment = 'Business';
        }

        else if ((courseName == 'Other') || !courseName)
            obj.UncDepartment = '';
    }

    public validate(obj: any, originalObj: any, lookups: any) {
        obj.errors = {};
        obj.warnings = {};
        obj.isNew = false;
        obj.isChanged = false;

        let primaryCourseName = obj.UncPrimaryCourseName?.name ?? obj.UncPrimaryCourseName;
        let originalPrimaryCourseName = originalObj.UncPrimaryCourseName?.name ?? originalObj.UncPrimaryCourseName;

        // Error related validation
        if (!obj.HostProgram)
            obj.errors['HostProgram'] = this.formatError('Institution', 'is required');
        else
            obj.HostCountry = lookups.universityCountryList[obj.HostProgram];

        if (!obj.HostCourseName?.trim())
            obj.errors['HostCourseName'] = this.formatError('Host Course Name', 'is required');

        if (obj.HostCourseName?.length > 200)
            obj.errors['HostCourseName'] = this.formatError('Host Course Name', 'can be 200 characters max');

        if (!!obj.hasAoE && !!this.showAoE) {

            if (!obj.AreaOfEmphasis)
                obj.errors['AreaOfEmphasis'] = this.formatError('Emphasis', 'must be selected');

            else {
                if (lookups.discontinuedAreas.includes(obj.AreaOfEmphasis))
                    obj.warnings['AreaOfEmphasis'] = 'This AOE is being discontinued. Only students who have previously declared this AOE should select this option.'

                //  Justification only required for a new request or when a non-approved AOE is selected.
                if ((!originalObj?.AreasOfEmphasis || !originalObj.AreasOfEmphasis.includes(obj.AreaOfEmphasis)) && !obj.AoeJustification?.trim())
                    obj.errors['AoeJustification'] = this.formatError('Emphasis Justification', 'is required');

                if (obj.AoeJustification?.length > 1000)
                    obj.errors['AoeJustification'] = this.formatError('Emphasis Justification', 'can be 1000 characters max');
            }
        }

        if (obj.HostCourseCreditHours <= 0)
            obj.errors['HostCourseCreditHours'] = this.formatError('Host Credit Hours', 'must be greater than 0');

        if (obj.HostCourseCreditHours >= 100)
            obj.errors['HostCourseCreditHours'] = this.formatError('Host Credit Hours', 'must be less than 100');

        if (!obj.HostCourseNumber)
            obj.errors['HostCourseNumber'] = this.formatError('Host Course Number', 'is required');

        if ((obj.HostCourseNumber == 'Other') && !obj.otherHostCourseNumber?.trim())
            obj.errors['otherHostCourseNumber'] = this.formatError('Other Host Course Number', 'is required');

        if (obj.otherHostCourseNumber?.length > 25)
            obj.errors['otherHostCourseNumber'] = this.formatError('Other Host Course Number', 'can be 25 characters max');

        if (typeof obj.UncPrimaryCourseName == 'string') {
            if (!obj.UncPrimaryCourseName?.trim())
                obj.errors['UncPrimaryCourseName'] = this.formatError('UNC Course Name', 'is required');

            if (obj.UncPrimaryCourseName?.length > 150)
                obj.errors['UncPrimaryCourseName'] = this.formatError('UNC Course Name', 'can be 150 characters max');
        }

        if ((primaryCourseName == 'Other') || (primaryCourseName == 'UNC Equivalent Business Elective Credit')) {

            if (!obj.otherPrimaryCourseName?.trim())
                obj.errors['otherPrimaryCourseName'] = this.formatError('Other UNC Course Name', 'is required');

            if (obj.otherPrimaryCourseName?.length > 150)
                obj.errors['otherPrimaryCourseName'] = this.formatError('Other UNC Course Name', 'can be 150 characters max');
        }
        else
            obj.otherPrimaryCourseName = '';

        if (primaryCourseName == 'General Education Requirement') {
            if (!obj.GeRequirement)
                obj.errors['GeRequirement'] = this.formatError('General Education Requirement', 'is required');
        }

        if (!obj.UncDepartment)
            obj.errors['UncDepartment'] = this.formatError('UNC Department', 'must be selected');

        if (!obj.UncPrimaryCourseNumber?.trim())
            obj.errors['UncPrimaryCourseNumber'] = this.formatError('UNC Course Number', 'is required');

        if (obj.UncPrimaryCourseNumber?.length > 15)
            obj.errors['UncPrimaryCourseNumber'] = this.formatError('UNC Course Number', 'can be 15 characters max');

        if (!!obj.UncPrimaryCourseNumber && (obj.UncPrimaryCourseNumber.length <= 15)) {
            if (primaryCourseName == 'Other') {
                if (obj.UncPrimaryCourseNumber?.toLowerCase()?.startsWith("busi"))
                    obj.errors['UncPrimaryCourseNumber'] = this.formatError('UNC Course Number', 'only UNC Equivalent Business Elective Credits should start with BUSI');
            }

            if (primaryCourseName == 'UNC Equivalent Business Elective Credit') {
                if (!obj.UncPrimaryCourseNumber?.toLowerCase()?.startsWith("busi"))
                    obj.errors['UncPrimaryCourseNumber'] = this.formatError('UNC Course Number', 'should start with BUSI for UNC Equivalent Business Elective Credit.  Any other course numbers should select "Other" for the UNC Course Name');
            }
        }

        if (obj.UncPrimaryCreditHours <= 0)
            obj.errors['UncPrimaryCreditHours'] = this.formatError('UNC Credit Hours', 'must be greater than 0');

        if (obj.UncPrimaryCreditHours >= 100)
            obj.errors['UncPrimaryCreditHours'] = this.formatError('UNC Credit Hours', 'must be less than 100');

        if (!obj.Term)
            obj.errors['Term'] = this.formatError('Term Abroad', 'must be selected');

        //  Change related validation
        //  If this is a new request not opened from a draft or from an already approved course, flag appropriately.
        if (!originalObj?.Id)
            obj.isNew = true;
        //  Otherwise we need to check fields against the existing PAC.
        else {
            //  If this isn't already approved then we know we're working with a new request that was re-opened from a draft.
            if (originalObj.ApprovalStatus != 'Approved')
                obj.isNew = true;
            //  If this is an already approved request then we can allow a new Host Course Number, AoE, or a term outside
            //  of the approval window.  Anything else and this should become a new request after this window is saved.
            else {
                const staticFields = ["HostProgram", "HostCountry", "HostCourseName", "HostCourseCreditHours", "UncDepartment",
                    "UncPrimaryCourseNumber", "UncPrimaryCreditHours", "otherPrimaryCourseName"];

                for (let field of staticFields) {
                    if (obj[field] != originalObj[field]) {
                        obj.isNew = true;
                        continue;
                    }
                }

                if (primaryCourseName != originalPrimaryCourseName)
                    obj.isNew = true;

                //  Now we check for allowed changes as long as this isn't a new request.
                if (!obj.IsNew) {
                    if (obj.HostCourseNumber == 'Other')
                        obj.isChanged = true;

                    if (!!obj.AreaOfEmphasis && !originalObj.AreasOfEmphasis.includes(obj.AreaOfEmphasis))
                        obj.isChanged = true;

                    //  No need to jump into all this logic if we already know the request is changed.
                    if (!obj.isChanged && !!obj.Term) {
                        const term = obj.Term.split(' ');
                        let month = null;
                        //  Just a reminder, months are at a base 0 when constructing a js Date.
                        switch (term[0]) {
                            case 'Spring':
                                month = 0;
                                break;
                            case 'Summer':
                                month = 4;
                                break;
                            case 'Fall':
                                month = 7;
                                break;
                        }

                        const termDate = new Date(term[1], month, 1);
                        const approvalEnd = new Date(originalObj.ApprovalEnd);
                        const approvalEndCleaned = new Date(approvalEnd.getFullYear(), approvalEnd.getMonth(), 1);

                        if (termDate > approvalEndCleaned)
                            obj.isChanged = true;
                    }
                }
            }
        }

        //  The syllabus is required if this is a new or changed request so we need to validate it
        if (obj.isNew || obj.isChanged) {
            const syllabus = this.syllabusInput?.nativeElement?.files[0];

            if (!syllabus && !obj.SyllabusFileType)
                obj.errors['syllabus'] = this.formatError('Syllabus', 'is required');

            else if(!!syllabus) {
                if (syllabus.size > UtilityService.maxFileSize)
                    obj.errors['syllabus'] = this.formatError('Syllabus', 'cannot be larger than 4MB');

                if ((!!syllabus?.size && !syllabus?.type) || (!!syllabus?.type && (!this.acceptedFileTypes.includes(syllabus.type))))
                    obj.errors['syllabus'] = this.formatError('Syllabus', 'must be a Word or PDF file');
            }
        }

        obj.hasErrors = !!Object.keys(obj.errors).length;
    }

    private formatError(field: string, message: string): string {
        return `${field} ${message}.`;
    }

    public syllabusChanged(obj: any) {
        const syllabus = this.syllabusInput?.nativeElement?.files[0];
        obj.SyllabusFilename = syllabus?.name;
        obj.SyllabusFileType = syllabus?.type;
        obj.onChange();
    }

    public onSave(obj: any, submit: boolean = false) {
        let succeeded = false;
        const syllabus = this.syllabusInput?.nativeElement?.files[0];

        //  Clean these up before we work through the fields
        if (!obj.hasAoE) {
            obj.AreaOfEmphasis = '';
            obj.AoeJustification = '';
        }

        const submittableFields = ["Id", "HostProgram", "HostCountry", "HostCourseName", "HostCourseCreditHours", "HostCourseNumber",
            "AreaOfEmphasis", "AoeJustification", "UncDepartment", "UncPrimaryCourseName", "UncPrimaryCourseNumber",
            "UncPrimaryCreditHours", "Term", "ApprovalStatus", "GeRequirement"];

        let request: any = {}
        for (let field of submittableFields)
            request[field] = obj[field];

        //  Transformations required before saving
        if (request.HostCourseNumber == 'Other')
            request.HostCourseNumber = obj.otherHostCourseNumber;

        if (request.UncPrimaryCourseName?.display)
            request.UncPrimaryCourseName = request.UncPrimaryCourseName.name;

        if (request.GeRequirement?.Key)
            request.GeRequirement = request.GeRequirement.Key;

        if (!!obj.otherPrimaryCourseName)
            request.UncPrimaryCourseName = obj.otherPrimaryCourseName;

        if (obj.isNew && (obj.ApprovalStatus == 'Approved'))
            request.Id = 0;

        request.isSubmitted = submit;
        request.RequestId = obj?.RequestId ?? 0;

        obj.isSaving = true;

        this.tcSubmit$ = this.transferCredits.saveRequest(request).pipe(
            switchMap(res => {
                // If file exists and save was successful, upload the syllabus
                if ((obj.isNew || obj.isChanged) && !!syllabus && res?.Request?.Id && res?.Course?.Id) {
                    return this.transferCredits.updateSyllabus(syllabus, res.Request.Id).pipe(
                        tap(updatedFile => {
                            this.log("updateSyllabus", `updated syllabus`, LogCategory.SubscriptionData);
                            obj.SyllabusFilename = updatedFile.SyllabusFilename;
                            obj.SyllabusFileType = updatedFile.SyllabusFileType;

                            if (!!this.request)
                                this.activeModal.close(res.Course);
                            else
                                this.close.emit();

                            succeeded = true;
                            return res;
                        })
                    );
                }
                else {
                    if (!!this.request)
                        this.activeModal.close(res.Course);
                    else
                        this.close.emit();

                    succeeded = true;
                    return of(res);
                }
            }),
            catchError((error) => {
                this.toastr.error(error?.error ?? 'Unable to save request');
                succeeded = false;
                return of(obj);
            }),
            finalize(() => {
                if (succeeded) {
                    if (submit)
                        this.toastr.success("Request submitted");
                    else
                        this.toastr.success("Request saved to drafts");
                    this.transferCredits.refreshSubject$.next(true);
                }

                obj.isSaving = false;
            }),
        );
    }
}
